import HttpClient from 'server/helpers/HttpClient';
import { formatMaskedAccountNumber, formatMaskedCardNumber } from 'shared/PaymentHelpers';
import { validateMerchant, getAPPayload, getMaskedAPPayment } from './ApplePay';
import { handlePaypalOrder, checkIFPaypalEnabled } from './Paypal';
import {
  getBaseRequestBody,
  submitPayment,
  convertLineItemsToSCSPaymentFormat,
  cleanupIdempotencyKey,
} from './Common';
import { adyenPaymentStatusTranslator } from 'server/services/serviceUtils';

const httpClient = HttpClient.getInstance();
import { accountTypeTranslator } from 'store/wallet/helpers';
import {
  getWeakTicket,
  getWalletAccount,
  capitalizeFirstLetter,
  convertToNullIfUndefined,
} from 'shared/clientUtils';
import { isPaymentRequest } from 'shared/utils';
import { getPaymentRequestBody } from './PaymentRequestHelpers';
import { isMAIPasPDF, getPayorEmail } from './helpers';
import {
  groupLineItems,
  calculateFreightAmount,
  calculateDiscountAmount,
} from 'businessLogic/server/scsToIcnAdapter';
import { handleNanopayPayment } from './Nanopay';
import { CP_ERROR_CODES, PAYMENT_ERROR_TYPES } from 'types/constants';
import { saleSelectors } from 'store/sale/selectors';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { handlePbbOrder } from './PayByBank';
import { handlePbcOrder } from './PayByCard';
import {
  createAffirmBnplOrder as affirmBnplCreateOrder,
  confirmAffirmBnplOrder as affirmBnplConfirmOrder,
} from './AffirmBnpl';
import { emailRegex } from 'shared/freeTextModalValidators';

/**
 * @type {import('./index').Payment}
 */
class Payment {
  constructor({ initialState, config, auth, companyInfo, insight, sale }) {
    this.config = config;
    this.auth = auth;
    this.companyInfo = companyInfo;
    this.sale = sale;
    this.insight = insight;
    this._data = initialState.payment;
  }
  /**
   * creates request from Client to SSR to initiate pbb payment
   */
  async handlePbbOrder(state) {
    let pbbStatus;
    const {
      insight: { token },
      payment: { amount },
    } = state;
    try {
      const payorEmail = this.contactDetailsEmail;
      const result = await handlePbbOrder(state, payorEmail);
      pbbStatus = adyenPaymentStatusTranslator(result?.status);
      if (result?.payByBankRedirectUrl) {
        window?.navigation
          ? window.navigation.navigate(result.payByBankRedirectUrl)
          : (window.location.href = result.payByBankRedirectUrl);
      } else {
        cleanupIdempotencyKey(token, amount);
        return pbbStatus;
      }
    } catch (e) {
      const { message = '' } = e;
      cleanupIdempotencyKey(token, amount);
      return { status: pbbStatus, message };
    }
  }

  async handlePbcOrder(state, walletInfo, isSavePaymentMethodChecked) {
    const {
      insight: { token },
      payment: { amount },
    } = state;
    try {
      const payorEmail = this.contactDetailsEmail;
      const result = await handlePbcOrder(
        state,
        walletInfo,
        payorEmail,
        isSavePaymentMethodChecked
      );

      if (result.type === 'redirect') {
        window?.navigation
          ? window.navigation.navigate(result.redirectUrl)
          : (window.location.href = result.redirectUrl);
      } else {
        return {
          type: result.type,
          status: result.status,
          orderId: result.orderId,
          paymentResponse: result.paymentResponse,
        };
      }
    } catch (e) {
      const { message = '' } = e;
      cleanupIdempotencyKey(token, amount);
      return { status: 'error', message };
    }
  }

  /**
   * creates request from Client to SSR to create a scheduled payment
   */
  async schedulePayment({ domainId, token, data }) {
    const { ssrtid, portal } = this.config;

    const url = `/${portal}/rest/sale/schedulePayment/${token}`;
    const endpoint = `/rest/sale/schedulePayment`;

    const headers = this._getScheduleHeaders({ domainId, token });

    data = this._addBiocatchSession(data);

    return await httpClient({
      url,
      method: 'POST',
      headers,
      endpoint,
      token,
      ssrtid,
      data,
    });
  }

  async deleteSchedulePayment({ domainId, token, id }) {
    const { ssrtid, portal } = this.config;

    const url = `/${portal}/rest/sale/schedulePayment/${token}/${id}`;
    const endpoint = `/rest/sale/schedulePayment`;

    const headers = this._getScheduleHeaders({ domainId, token });

    return await httpClient({
      url,
      method: 'DELETE',
      headers,
      endpoint,
      token,
      ssrtid,
    });
  }

  getBaseRequestBody({
    paymentMethod,
    amountPaid,
    maskedPayment,
    gratuityAmount = 0,
    displayGratuityFeature = false,
    isAddL3Fields = false,
    featureFlags = {},
    frictionlessCheckout,
    saveToMerchantWallet,
  }) {
    const { riskProfileToken: riskProfileToken, paymentProcessor } = this.insight;
    const {
      type: saleType,
      currency,
      customerName,
      saleId,
      isGpu,
      _data,
      convenienceFee,
    } = this.sale;
    const { entityId: payorAuthId } = this.auth;
    const { bioCatchSessionId } = this.config;

    let basicRequestBody = {
      paymentBasicSaleInfo: {
        saleId,
        saleType,
        amountPaid: amountPaid.toFixed(2),
        ...(displayGratuityFeature &&
          gratuityAmount &&
          typeof gratuityAmount === 'number' && { gratuityAmount: gratuityAmount.toFixed(2) }),
        payorAuthId,
        paymentMethod,
        paymentProcessor,
        customerName,
        currency,
        riskProfileToken,
        maskedPayment,
        isGpu,
        bioCatchSessionId,
        ccSurchargeFeeAmountEnabled: convenienceFee?.cc?.enabled,
      },
      // Pass IXP experiments to server (only if set)
      // Server reads this instead of making IXP API calls
      ...(this._ixpExperiments &&
        Object.keys(this._ixpExperiments).length > 0 && { ixpExperiments: this._ixpExperiments }),
    };
    if (saleSelectors.achOnlineConvenienceFeeEnabledSelector(this.sale)) {
      const amountPaid =
        gratuityAmount && typeof gratuityAmount === 'number'
          ? (saleSelectors.amountSelector(this.sale) + gratuityAmount).toFixed(2)
          : saleSelectors.amountSelector(this.sale).toFixed(2);

      basicRequestBody.paymentBasicSaleInfo.feeAmount =
        saleSelectors.achOnlineConvenienceFeeAmountSelector(this.sale);
      basicRequestBody.paymentBasicSaleInfo.amountPaid = amountPaid;
    }
    if (isAddL3Fields && _data) {
      const { amount, receivable } = _data;

      basicRequestBody = {
        ...basicRequestBody,
        paymentBasicSaleInfo: {
          ...basicRequestBody.paymentBasicSaleInfo,
          applyTaxAfterDiscount: convertToNullIfUndefined(
            receivable && receivable.applyTaxAfterDiscount
          ),
          originalSaleAmount: convertToNullIfUndefined(amount),
          saleBalanceBeforePayment: convertToNullIfUndefined(receivable && receivable.balance),
        },
      };
    }
    const payorEmail = saleSelectors.toEmailsSelector(this.sale)?.[0];
    const { isSelected = false, email: frictionlessCheckoutEmail = undefined } =
      frictionlessCheckout || {};
    const isMaipPDF = isMAIPasPDF({ sale: this.sale, featureFlags });
    const isOneToMany = saleSelectors.isOneToManyPaymentRequest(this.sale);
    if (isSelected) {
      basicRequestBody = {
        ...basicRequestBody,
        identitySessionData: {
          emailAddress: isMaipPDF || isOneToMany ? this.contactDetailsEmail : payorEmail,
          isFrictionlessAuth: true,
        },
      };
    }
    if (frictionlessCheckoutEmail && payorEmail && frictionlessCheckoutEmail !== payorEmail) {
      basicRequestBody = {
        ...basicRequestBody,
        paymentBasicSaleInfo: {
          ...basicRequestBody.paymentBasicSaleInfo,
          additionalPayorRecipient: frictionlessCheckoutEmail,
        },
      };
    }
    // Add notificationData for merchant wallet save
    if (saveToMerchantWallet) {
      const notificationPayorEmail = getPayorEmail({
        isUserSignedIn: this.auth?.isUserSignedIn,
        authUsername: this.auth?.username,
        frictionlessCheckoutEmail,
        payorEmail,
      });

      basicRequestBody = {
        ...basicRequestBody,
        notificationData: {
          payorEmail: notificationPayorEmail,
          merchantEmail:
            companyInfoSelectors.emailSelector(this.companyInfo) ||
            companyInfoSelectors.emailSelector(this.companyInfo?._data) ||
            '',
          merchantName:
            companyInfoSelectors.nameSelector(this.companyInfo) ||
            companyInfoSelectors.nameSelector(this.companyInfo?._data) ||
            '',
        },
      };
    }

    if (isPaymentRequest(saleType)) {
      return getPaymentRequestBody({ basicRequestBody, sale: this.sale, payment: this });
    } else if (isMaipPDF) {
      return {
        ...basicRequestBody,
        contactInfo: {
          emails: [this.contactDetailsEmail],
        },
      };
    } else {
      return basicRequestBody;
    }
  }

  generateBankWalletPayload({ walletAccount }) {
    const { bankCode, accountType, payorFirstName, payorLastName, id } = walletAccount;
    const additionalInfo = {
      achInfo: {
        bankCode,
        payorName: `${payorFirstName}${payorLastName ? ` ${payorLastName}` : ''}`,
        accountType: accountTypeTranslator.fromCpServerToWallet(accountType),
        phoneNumber: '0000000000',
      },
      walletInfo: {
        id,
      },
    };
    return additionalInfo;
  }

  async submitBankPayment({
    bankDetails,
    wallet,
    amountPaid,
    timeout,
    gratuityAmount = 0,
    displayGratuityFeature = false,
    featureFlags,
    frictionlessCheckout,
  }) {
    const { portal, ssrtid } = this.config;
    const { domainId, token, companyId } = this.insight;
    const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = this.auth;
    const { selectedWalletId, userWallets, cardOnFile } = wallet;
    const saleType = this.sale.type;
    let additionalACHInfo, paymentMethod, maskedPayment;
    if (userWallets && userWallets.length && selectedWalletId !== 'AddBank') {
      const walletAccount = getWalletAccount(userWallets, selectedWalletId);
      const { accountNumber, accountType } = walletAccount;
      maskedPayment = `${capitalizeFirstLetter(accountType)} ${formatMaskedAccountNumber(
        accountNumber
      )}`;
      additionalACHInfo = this.generateBankWalletPayload({ walletAccount });
      paymentMethod = 'bank wallet';
    } else {
      paymentMethod = 'bank';
      const { accountNumber, accountType, phone: phoneNumber, name } = bankDetails;
      const achInfo = {
        payorName: name,
        phoneNumber,
        accountType,
        tokenNumber: bankDetails.token, // If we are using Wallet Service REST2 and wallet-rest2-api-tokenize-bank is true, then in walletTokenizationPaymentCreation we had tokenized the raw data and saved it in bankDetails
      };
      additionalACHInfo = {
        achInfo: achInfo,
        savePaymentMethod: this.isSavePaymentMethodChecked ? 'AddBank' : undefined,
      };
      const formattedAccountType = capitalizeFirstLetter(
        accountTypeTranslator.fromWalletToCpServer(accountType)
      );
      maskedPayment = `${formattedAccountType} ${formatMaskedAccountNumber(accountNumber)}`;
    }
    const saveToMerchantWallet = cardOnFile?.isCheckboxChecked ? true : undefined;
    const customerId = this.sale.contact?.entity?.globalId?.localId;

    const baseRequestBody = this.getBaseRequestBody({
      paymentMethod,
      amountPaid,
      maskedPayment,
      ...(displayGratuityFeature && gratuityAmount && { gratuityAmount, displayGratuityFeature }),
      featureFlags,
      frictionlessCheckout,
      saveToMerchantWallet,
    });

    const data = {
      ...baseRequestBody,
      ...additionalACHInfo,
      saveToMerchantWallet,
      saleInfo: {
        saleType,
        companyId,
        customerId,
      },
    };

    const result = await submitPayment({
      data,
      portal,
      ssrtid,
      domainId,
      token,
      ticket,
      realmId,
      isUserSignedIn,
      authToken,
      syncToken,
      entityId,
      timeout,
    });
    return result;
  }

  generateCardWalletPayload({ enabledCCTypes, walletAccount }) {
    // Payment using an existing wallet pm
    if (
      walletAccount &&
      walletAccount.cardType === 'amex' &&
      enabledCCTypes.indexOf('amex') === -1
    ) {
      return {
        data: { message: 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID', cardType: 'american-express' },
      };
    }
    const { payorFirstName: payorName, cardNumber: tokenNumber, id } = walletAccount;
    const additionalInfo = {
      creditCardInfo: {
        tokenNumber,
        payorName,
      },
      walletInfo: {
        id,
      },
    };
    return additionalInfo;
  }

  async submitCardPayment({
    // Payment using a new card (not saved in the wallet)
    ccDetails,
    wallet,
    amountPaid,
    timeout,
    gratuityAmount,
    displayGratuityFeature = false,
    featureFlags = {},
    frictionlessCheckout,
  }) {
    const { selectedWalletId, userWallets, enabledCCTypes, cardOnFile } = wallet;
    const { portal, ssrtid } = this.config;
    const { domainId, token, companyId } = this.insight;
    const saleType = this.sale.type;

    const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = this.auth;
    let additionalCCInfo, paymentMethod, maskedPayment;
    if (userWallets && userWallets.length && selectedWalletId !== 'AddCard') {
      paymentMethod = 'credit card wallet';
      const walletAccount = getWalletAccount(userWallets, selectedWalletId);
      const { cardType, cardNumber } = walletAccount;
      maskedPayment = formatMaskedCardNumber(cardType, cardNumber);
      additionalCCInfo = this.generateCardWalletPayload({ enabledCCTypes, walletAccount });
    } else {
      const { cardType, name: payorName, token: tokenNumber, cardNumber } = ccDetails;

      paymentMethod = 'credit card';
      maskedPayment = formatMaskedCardNumber(cardType, cardNumber);
      additionalCCInfo = {
        creditCardInfo: {
          tokenNumber,
          payorName,
        },
        savePaymentMethod: this.isSavePaymentMethodChecked ? 'AddCard' : undefined,
      };
    }

    const isAddL3Fields = featureFlags['add-L3-fields-card-payment'];
    const saveToMerchantWallet = cardOnFile?.isCheckboxChecked ? true : undefined;
    const customerId = this.sale.contact?.entity?.globalId?.localId;

    const baseRequestBody = this.getBaseRequestBody({
      paymentMethod,
      amountPaid,
      maskedPayment,
      ...(displayGratuityFeature && gratuityAmount && { gratuityAmount, displayGratuityFeature }),
      isAddL3Fields,
      featureFlags,
      frictionlessCheckout,
      saveToMerchantWallet,
    });

    const data = {
      ...baseRequestBody,
      ...additionalCCInfo,
      saveToMerchantWallet,
      saleInfo: {
        id: this.sale.saleId,
        freightAmount: calculateFreightAmount(this.sale.freight),
        discountAmount: calculateDiscountAmount(this.sale.lines),
        taxAmount: this.sale.tax || 0,
        lines: convertLineItemsToSCSPaymentFormat(groupLineItems(this.sale.lines), isAddL3Fields),
        saleType,
        companyId,
        customerId,
      },
    };
    const result = await submitPayment({
      data,
      portal,
      ssrtid,
      domainId,
      token,
      ticket,
      realmId,
      isUserSignedIn,
      authToken,
      syncToken,
      entityId,
      timeout,
    });
    return result;
  }

  async submitNanopayPayment({ nanopayDetails, amountPaid, timeout }) {
    return handleNanopayPayment({
      config: this.config,
      auth: this.auth,
      insight: this.insight,
      sale: this.sale,
      nanopayDetails,
      amountPaid,
      timeout,
      getBaseRequestBody,
      getRequestBody: this.getBaseRequestBody.bind(this),
      submitPayment,
    });
  }

  submitApplePayPayment({ amountPaid, timeout, applePayInfo, featureFlags, frictionlessCheckout }) {
    let maskedPayment, basicRequestBody, data;
    const {
      saleId,
      freight,
      tax,
      lines,
      type: saleType,
      currency,
      customerName,
      _data = {},
    } = this.sale;
    const { isGpu } = _data;
    const {
      riskProfileToken: riskProfileToken,
      paymentProcessor,
      domainId,
      token,
      companyId,
    } = this.insight;
    const { portal, ssrtid, bioCatchSessionId } = this.config;
    const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = this.auth;

    maskedPayment = getMaskedAPPayment(applePayInfo);
    basicRequestBody = getBaseRequestBody({
      paymentMethod: 'applePay',
      maskedPayment,
      amountPaid,
      riskProfileToken,
      paymentProcessor,
      saleId,
      freight,
      tax,
      lines,
      saleType,
      currency,
      customerName,
      bioCatchSessionId,
    });

    if (isPaymentRequest(saleType)) {
      basicRequestBody = getPaymentRequestBody({
        basicRequestBody,
        sale: this.sale,
        payment: this,
      });
    }
    const payorEmail = saleSelectors.toEmailsSelector(this.sale)?.[0];
    const { isSelected = false, email: frictionlessCheckoutEmail = undefined } =
      frictionlessCheckout || {};
    const isMaipPDF = isMAIPasPDF({ sale: this.sale, featureFlags });
    const isOneToMany = saleSelectors.isOneToManyPaymentRequest(this.sale);
    if (isSelected) {
      basicRequestBody = {
        ...basicRequestBody,
        identitySessionData: {
          emailAddress: isMaipPDF || isOneToMany ? this.contactDetailsEmail : payorEmail,
          isFrictionlessAuth: true,
        },
      };
      if (frictionlessCheckoutEmail && payorEmail && frictionlessCheckoutEmail !== payorEmail) {
        basicRequestBody = {
          ...basicRequestBody,
          paymentBasicSaleInfo: {
            ...basicRequestBody.paymentBasicSaleInfo,
            additionalPayorRecipient: frictionlessCheckoutEmail,
          },
        };
      }
    }
    data = {
      ...getAPPayload(basicRequestBody, applePayInfo, isGpu),
      saleInfo: {
        ...basicRequestBody.saleInfo,
        saleType,
        companyId,
      },
    };
    if (isMaipPDF) {
      data = {
        ...data,
        contactInfo: {
          emails: [this.contactDetailsEmail],
        },
      };
    }

    return submitPayment({
      data,
      portal,
      ssrtid,
      domainId,
      token,
      ticket,
      realmId,
      isUserSignedIn,
      authToken,
      syncToken,
      entityId,
      timeout,
    });
  }

  async submitSCSPayment({
    ccDetails,
    bankDetails,
    nanopayDetails,
    applePayInfo,
    wallet,
    amountPaid,
    paymentMethodType,
    timeout,
    displayGratuityFeature,
    gratuityAmount,
    featureFlags = {},
    frictionlessCheckout,
    ixp,
  }) {
    // Store IXP experiments for use in getBaseRequestBody
    // This allows server to check experiment treatment without making IXP API calls
    this._ixpExperiments = ixp;

    let result;
    try {
      if (!(wallet || applePayInfo) || !amountPaid || !paymentMethodType) {
        throw Error(`submitSCSPayment: Invalid arguments`);
      }

      switch (paymentMethodType) {
        case 'bank':
        case 'eft':
          result = await this.submitBankPayment({
            bankDetails,
            wallet,
            amountPaid,
            timeout,
            ...(displayGratuityFeature &&
              gratuityAmount && { gratuityAmount, displayGratuityFeature }),
            featureFlags,
            frictionlessCheckout,
          });
          break;
        case 'cc':
        case 'dc':
        case 'dc,cc':
          result = await this.submitCardPayment({
            ccDetails,
            wallet,
            amountPaid,
            timeout,
            ...(displayGratuityFeature &&
              gratuityAmount && { gratuityAmount, displayGratuityFeature }),
            featureFlags,
            frictionlessCheckout,
          });
          break;
        case 'nanopay':
          result = await this.submitNanopayPayment({ nanopayDetails, amountPaid, timeout });
          break;
        case 'ap':
          result = await this.submitApplePayPayment({
            amountPaid,
            timeout,
            applePayInfo,
            featureFlags,
            frictionlessCheckout,
          });
          break;
        default:
          throw Error(`submitSCSPayment: Invalid payment method - ${paymentMethodType}`);
      }
      if (!result || result.status !== 200) {
        result = {
          data: {
            paymentStatus: 'ERROR',
            status: result.status,
            statusText: result.statusText,
          },
        };
      } else {
        result.data.status = result.status;
        result.data.statusText = result.statusText;
      }
    } catch (e) {
      let errorData = {};
      const { response, config } = e;
      if (response) {
        const { status, statusText, data } = response;
        if (data) {
          const { ErrorMessage } = data;
          errorData = {
            ...errorData,
            status,
            statusText,
            errorCode:
              ErrorMessage === 'SCS Sale Validation Error'
                ? CP_ERROR_CODES.SALE_VALIDATION
                : ErrorMessage === 'PAYMENTS_NOT_AVAILABLE_FOR_INVOICE'
                ? PAYMENT_ERROR_TYPES.PAYMENT_ERROR_V2
                : '',
          };
        } else {
          errorData = {
            ...errorData,
            status,
            statusText,
          };
        }
      }

      result = {
        data: {
          ...errorData,
          paymentStatus: 'ERROR',
        },
        config,
      };
      if (e.code) {
        result = { ...result, response: { status: e.code, data: errorData } };
      } else if (e.response && e.response.status) {
        result = { ...result, response: { status: e.response.status, data: errorData } };
      }
    }

    return result;
  }

  async handlePaypalOrder({
    action,
    intuitOrderId,
    usedPaymentMethod,
    gratuityAmount,
    paymentAmount,
    contactDetailsName,
    contactDetailsEmail,
    featureFlags,
    additionalPayorRecipient,
  }) {
    const { riskProfileToken: profileToken, domainId, token } = this.insight;
    const { portal, ssrtid } = this.config;
    const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = this.auth;
    const {
      currency: currencyCode,
      type,
      contact,
      isGpu,
      subType,
      contact: { displayName, toEmails } = {},
    } = this.sale;
    const headers = getWeakTicket({
      domainId,
      entityId,
      realmId,
      token,
      ticket,
      isUserSignedIn,
      syncToken,
      authToken,
      ssrtid,
    });
    return await handlePaypalOrder({
      action,
      intuitOrderId,
      headers,
      portal,
      authToken,
      amount: paymentAmount,
      currencyCode,
      isGpu,
      subType,
      displayName,
      toEmails,
      contactDetailsName,
      contactDetailsEmail,
      usedPaymentMethod,
      gratuityAmount,
      type,
      profileToken,
      token,
      ssrtid,
      featureFlags,
      contact,
      additionalPayorRecipient,
    });
  }

  checkIFPaypalEnabled({
    sourceOffering,
    saleType,
    enabledPaymentMethods,
    featureFlags,
    paymentMethodType,
    userWallets,
  }) {
    return checkIFPaypalEnabled({
      sourceOffering,
      saleType,
      enabledPaymentMethods,
      featureFlags,
      paymentMethodType,
      userWallets,
    });
  }

  async createAffirmBnplOrder(state) {
    return await affirmBnplCreateOrder(state);
  }

  async confirmAffirmBnplOrder(state, checkoutId, email) {
    return await affirmBnplConfirmOrder(state, checkoutId, email);
  }

  async validateApplePayMerchant(validationURL) {
    const { portal, ssrtid } = this.config;
    const { domainId, token } = this.insight;
    const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = this.auth;
    const headers = getWeakTicket({
      domainId,
      entityId,
      realmId,
      token,
      ticket,
      isUserSignedIn,
      syncToken,
      authToken,
      ssrtid,
    });
    const res = await validateMerchant(validationURL, portal, headers);
    return res;
  }

  set contactDetailsName(value) {
    this._data.contactDetailsName = value;
  }

  get contactDetailsName() {
    return this._data.contactDetailsName;
  }

  set contactDetailsEmail(value) {
    this._data.contactDetailsEmail = value;
  }

  get contactDetailsEmail() {
    return this._data.contactDetailsEmail;
  }

  get isContactDetailsNameValid() {
    const name = this.contactDetailsName;

    let isValid = false;
    if (typeof name === 'string') {
      isValid = /[a-zA-Z].+ [a-zA-Z].+/gm.test(name);
    }
    return isValid;
  }

  get isContactDetailsEmailValid() {
    const email = this.contactDetailsEmail;
    let isValid = false;
    if (typeof email === 'boolean') {
      isValid = email;
    } else if (typeof email === 'string') {
      isValid = emailRegex.test(email);
    }

    return isValid;
  }

  set isSavePaymentMethodChecked(value) {
    this._data.isSavePaymentMethodChecked = value;
  }

  get isSavePaymentMethodChecked() {
    return this._data.isSavePaymentMethodChecked;
  }

  async subscriptionPayment({ domainId, token, data }) {
    const { ssrtid, portal } = this.config;

    const url = `/${portal}/rest/sale/subscription/${token}`;
    const endpoint = `/rest/sale/subscription`;

    const headers = this._getScheduleHeaders({ domainId, token });

    data = this._addBiocatchSession(data);

    return await httpClient({
      url,
      method: 'POST',
      headers,
      endpoint,
      token,
      ssrtid,
      data,
      timeout: 15000, // For an Immediate Subscription the request takes much longer, because it reaches for PayApi for an immediate payment as well as activating the subscription for further payments
    });
  }

  _getScheduleHeaders({ domainId, token }) {
    const {
      ticket,
      authLevel,
      realmId,
      isUserSignedIn,
      isEntityPromoted,
      entityId,
      authToken,
      syncToken,
    } = this.auth;

    const headers = {
      'Intuit-ACSToken': token,
      'Intuit-AuthLevel': authLevel,
      'Intuit-DomainId': domainId,
      'Intuit-IntuitId': entityId,
      'Intuit-RealmId': realmId,
      isClientStateSignedIn: isUserSignedIn,
      isEntityPromoted: isEntityPromoted,
      sessionTicket: ticket,
      Authorization: `Bearer ${authToken}`,
    };

    if (syncToken) {
      headers['syncToken'] = syncToken;
    }

    return headers;
  }

  _addBiocatchSession(data) {
    const { biocatchSessionId } = this.insight;

    if (biocatchSessionId) {
      data = Object.assign({}, data, { biocatchSessionId });
    }

    return data;
  }

  async cancelSubscriptionPayment({ domainId, token }) {
    const { ssrtid, portal } = this.config;

    const url = `/${portal}/rest/sale/subscription/${token}`;
    const endpoint = `/rest/sale/subscription`;

    const headers = this._getScheduleHeaders({ domainId, token });

    try {
      return await httpClient({
        url,
        method: 'DELETE',
        headers,
        endpoint,
        token,
        ssrtid,
        data: {},
      });
    } catch (e) {
      return e;
    }
  }

  async reportPaymentFailure(error, paymentMethod) {
    const { ssrtid, portal } = this.config;
    try {
      const errorResponse = error?.response || error;
      const { domainId, token } = this.insight;
      const errorData = errorResponse?.data || {};
      const url = `/${portal}/rest/reporting/prometheus/payment-failure`;
      const headers = this._getScheduleHeaders({ domainId, token });

      await httpClient({
        url,
        method: 'POST',
        headers,
        token,
        ssrtid,
        data: {
          paymentMethod,
          errorCode: errorData?.code || errorData?.status || error?.status || 'unknown',
          errorType: errorData?.type || error?.name || 'Error',
        },
      });
    } catch (reportError) {
      /* empty */
    }
  }
}

export default Payment;
